<template>
  <div class="catalog-page">
    <div class="top-filters-wrapper">
      <div v-if="isLoading || isLoadingLocal" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>

      <div class="category-navigation">
        <div class="category-crumbs">
          <span
            v-for="(level, idx) in selectedCategoryPathComputed"
            :key="'cat-path-' + idx"
            @click="goToCategoryLevel(idx)"
            class="breadcrumb"
            :class="{ 'disabled-link': isLoading }"
          >
            {{ level }}
          </span>
        </div>
        <div class="subcategory-list" v-if="subcategories.length">
          <span
            v-for="subcat in subcategories"
            :key="subcat.name"
            class="subcategory-link"
            @click="drillDownCategory(subcat.name)"
            :class="{ 'disabled-link': isLoading }"
          >
            {{ subcat.name }} ({{ subcat.totalCount }})
          </span>
        </div>
      </div>

    <!-- SINGLE ATTRIBUTE FILTER BAR -->
    <div class="top-attributes-container">
      <div class="top-attributes-bar">
        <div class="attribute-dropdown">
          <label>Attribute:</label>
          <multiselect
            class="attribute-multiselect"
            v-model="selectedAttributeObj"
            :options="allAttributeOptions"
            track-by="displayName"   
            label="displayName"    
            placeholder="Select an attribute"
            :searchable="true"
            :close-on-select="true"
            :allow-empty="true"
            :select-label="''"  
            :selected-label="''" 
            :deselect-label="''" 
            :custom-label="attributeLabel"
          />
        </div>

        <div class="attribute-dropdown" v-if="selectedAttributeObj && selectedAttributeObj.values && selectedAttributeObj.values.length">
          <label>Value:</label>
          <multiselect
            class="attribute-multiselect"
            v-model="selectedValueObj"
            :options="selectedAttributeObj.values"
            track-by="valueCode"
            label="displayValue"
            placeholder="Select a value"
            :searchable="true"
            :close-on-select="true"
            :allow-empty="true"
            :select-label="''"  
            :selected-label="''" 
            :deselect-label="''" 
            :custom-label="valueLabel"
          />
        </div>
      </div>

        <div class="attribute-dropdown">
          <label>Color:</label>
          <select v-model="selectedColorValue" :disabled="isLoading">
            <option :value="null">Select a color</option>
            <option v-for="opt in colorOptions" :key="opt.value" :value="opt.value">
              {{ opt.value }} ({{ opt.count }})
            </option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>Quality Check:</label>
          <select v-model="qualityCheckModel" :disabled="isLoading">
            <option value="">None</option>
            <option value="OK">OK</option>
            <option value="REVIEW">REVIEW</option>
            <option value="NOT_OK">NOT OK</option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>Quality Check Flag:</label>
          <select v-model="qualityFlagFilterModel" :disabled="isLoading">
            <option value="">Select a flag</option>
            <option value="alliumInternalData.processingDetails.processingFlags.hasRetrievableImage">Image retrieval</option>
            <option value="alliumInternalData.processingDetails.processingFlags.gpcBackendMappingNotOk">GPC mapping</option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>Processed Time:</label>
          <select v-model="timeFilterModel" :disabled="isLoading">
            <option value="">Any time</option>
            <option value="1">Past day</option>
            <option value="3">Past 3 days</option>
            <option value="5">Past 5 days</option>
            <option value="10">Past 10 days</option>
            <option value="30">Past 30 days</option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>Taxonomy Version:</label>
          <select v-model="taxonomyFilterModel" :disabled="isLoading">
            <option value="">Any version</option>
            <option :value="`${t.name}::${t.updatedAt}`" v-for="t in taxonomyFilterOptions">{{ t.name }} ({{ t.updatedAt }})</option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>GPC Brick Unchanged:</label>
          <select v-model="gpcBrickUnchangedModel" :disabled="isLoading">
            <option value="">None</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>
        <div class="attribute-dropdown">
          <label>
            <input 
              type="checkbox" 
              v-model="showAttributeFlagsComputed" 
              :disabled="isLoading"
            />
            Show Products w/ Attribute Flags 
          </label>
        </div>
      </div>
    </div>

    <div class="catalog-layout">
      <div class="sidebar-wrapper">
        <div v-if="isLoading || isLoadingLocal" class="loading-overlay">
          <div class="loading-spinner"></div>
        </div>
        <aside class="filters-sidebar">
          <div
            v-for="fkey in primaryFilterKeys"
            :key="fkey"
            class="filter-section"
          >
            <h3>{{ formatFilterKey(fkey) }}</h3>

            <!-- Backend Category uses tree component -->
            <div v-if="fkey === 'backendCategory'">
              <BackendCategoryTree
                :categories="store.getBackendCategoryTree()"
                :selected-values="selectedFiltersComputed[fkey] || []"
                :disabled="isLoading"
                :counts="backendCategoryCounts"
                @toggle-select="toggleBackendCategory"
              />
            </div>

            <!-- All other filters use regular list -->
            <template v-else>
              <div v-if="fkey === 'brandId'" class="search-box">
                <input
                  type="text"
                  :value="brandSearchComputed"
                  @input="debouncedBrandSearch($event.target.value)"
                  placeholder="Search brands..."
                  :disabled="isLoading"
                />
              </div>

            <div v-else-if="fkey === 'webCategory'" class="search-box">
              <input
                type="text"
                :value="webCategorySearchComputed"
                @input="debouncedWebCategorySearch($event.target.value)"
                placeholder="Search Web Category..."
                :disabled="isLoading"
              />
            </div>

            <div v-else-if="fkey === 'materialName'" class="search-box">
              <input
                type="text"
                :value="alliumSearchComputed"
                @input="debouncedAlliumSearch($event.target.value)"
                placeholder="Search Material..."
                :disabled="isLoading"
              />
            </div>

            <div class="filter-options">
              <label
                v-for="opt in visibleFilterOptions(fkey)"
                :key="opt.value"
                class="filter-option"
              >
                <input
                  type="checkbox"
                  :value="opt.value"
                  :checked="isFilterActive(fkey, opt.value)"
                  @change="toggleFilter(fkey, opt.value)"
                  :disabled="isLoading"
                />
                <span>{{ opt.value }} ({{ opt.count }})</span>
              </label>
            </div>
            </template>
          </div>

          <div class="active-filters" v-if="hasActiveFilters">
            <h3>Active Filters</h3>
            <div class="filter-tags">
              <div
                v-for="(arr, key) in selectedFiltersComputed"
                :key="'active-'+key"
                class="filter-category"
              >
                <div
                  v-for="val in arr"
                  :key="val"
                  class="filter-tag"
                >
                  {{ val }}
                  <button @click="removeFilter(key, val)" class="remove-filter">×</button>
                </div>
              </div>
              <!-- Show the single attribute filter if set -->
              <div
                v-if="selectedAttrIdComputed && selectedAttrValueComputed"
                class="filter-category"
              >
                <div class="filter-tag">
                  {{ getAttrDisplayName(selectedAttrIdComputed) }}: {{ selectedAttrValueComputed }}
                  <button @click="clearSingleAttribute" class="remove-filter">×</button>
                </div>
              </div>
            </div>

          <button class="clear-filters" @click="clearAllFilters" :disabled="isLoading">
            Clear All Filters
          </button>
        </div>
      </aside>
      </div>
      <!-- PRODUCTS GRID -->
      <main class="products-content">
        <div class="results-summary" style="display: flex; justify-content: space-between;">
          <span class="pt-4">
            Showing page {{ currentPageComputed }} of {{ totalPages }}
            ({{ totalProducts }} total products)
          </span>
          <div>
            <v-btn
              v-if="products.length > 0"
              color="#2c3e50"
              variant="flat"
              class="reprocess-all-btn ma-2"
              @click="toggleSelectMode"
            >
              {{ selectMode ? "Cancel Selection" : "Select Products" }}
            </v-btn>

            <v-btn
              v-if="products.length > 0"
              :disabled="this.selectedCount === 0"
              color="#2c3e50"
              variant="flat"
              class="reprocess-all-btn ma-2"
              @click="openReprocessModal"
            >
              {{ selectMode ? `Reprocess ${this.selectedCount} selected` : 'Reprocess' }}
            </v-btn>
            <v-btn
              v-if="products.length > 0"
              color="#2c3e50"
              variant="flat"
              class="reprocess-all-btn"
              @click="rebuildFilterCounts"
            >
              Rebuild Filter Counts
            </v-btn>
          </div>
        </div>

        <div class="products-grid">
          <!-- Skeleton loaders when loading -->
          <template v-if="isLoading">
            <div
              v-for="n in pageSizeComputed"
              :key="'skeleton-' + n"
              class="product-card skeleton-card"
            >
              <div class="product-image skeleton-image"></div>
              <div class="product-info">
                <div class="skeleton-title"></div>
                <div class="skeleton-brand"></div>
                <div class="skeleton-category"></div>
              </div>
            </div>
          <!-- updated references for brand, category, images, etc. -->
          <div
            v-for="product in products"
            :key="product.id"
            class="product-card"
            @click="handleProductClick(product)"
          >

            <div class="product-image">
              <img :src="getDisplayImage(product)" @error="onImageError" />
            </div>
          </div>
          </template>

          <!-- Actual product cards when loaded -->
          <template v-else>
            <div
              v-for="product in products"
              :key="product.id"
              class="product-card"
              @click="handleProductClick(product)"
            >

              <!-- Selection marker for select mode -->
              <template v-if="selectMode">
                <span v-if="selectedIds.includes(product.id)" class="select-mark">
                  <svg fill="#000000" width="30" height="30" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><path d="M499 291q-56 0-104 28t-76 76-28 104 28 104 76 76 104 28 104-28 76-76 28-104-28-104-76-76-104-28zm0-208q-113 0-210 57-94 55-149 149-57 97-57 210t57 210q55 94 149 149 97 57 210 57t210-57q94-55 149-149 57-97 57-210t-57-210q-55-94-149-149-97-57-210-57zm0 749q-90 0-168-46-75-44-119-119-46-78-46-168t46-168q44-75 119-119 78-46 168-46t168 46q75 44 119 119 46 78 46 168t-46 168q-44 75-119 119-78 46-168 46z"/></svg>
                </span>
                <span v-else class="select-mark">
                  <svg fill="#000000" width="30" height="30" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><path d="M499 83q-113 0-210 57-94 55-149 149-57 97-57 210t57 210q55 94 149 149 97 57 210 57t210-57q94-55 149-149 57-97 57-210t-57-210q-55-94-149-149-97-57-210-57zm0 749q-90 0-168-46-75-44-119-119-46-78-46-168t46-168q44-75 119-119 78-46 168-46t168 46q75 44 119 119 46 78 46 168t-46 168q-44 75-119 119-78 46-168 46z"/></svg>
                </span>
              </template>

              <div class="product-image">
                <img :src="getDisplayImage(product)" @error="onImageError" />
              </div>
              <div class="product-info">
                <h3>{{ product.title }}</h3>
                <p class="product-brand">{{ product.brand }}</p>
                <p class="product-category">{{ product.category }}</p>
              </div>
            </div>
          </template>
        </div>

        <!-- NO RESULTS -->
        <div v-if="!isLoading && products.length === 0" class="no-products-message">
          No products match your filters
        </div>

        <!-- PAGINATION -->
        <div class="pagination-container" v-if="totalPages > 1">
          <ul class="pagination">
            <li class="arrow" :class="{ disabled: currentPageComputed===1 }" @click="prevPage">«</li>
            <li
              v-for="item in pagesToShow"
              :key="item"
              :class="{ active: item===currentPageComputed, dots: item==='...' }"
              @click="item!=='...' && goToPage(item)"
            >
              {{ item }}
            </li>
            <li class="arrow" :class="{ disabled: currentPageComputed===totalPages }" @click="nextPage">»</li>
          </ul>
        </div>

        <!-- PAGE SIZE -->
        <div class="page-size-container">
          <div class="filter-field">
            <label for="page-size">Items per page:</label>
            <select
              id="page-size"
              v-model.number="pageSizeComputed"
              class="filter-select"
            >
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
            </select>
          </div>
        </div>
      </main>
    </div>
<div v-if="showReprocessConfirm" class="modal-overlay">
      <div class="modal-content">
        <v-card flat>
          <v-card-title class="text-h5">
            Reprocess {{ selectedCount }} Articles?
          </v-card-title>

          <v-card-text>
            <div v-if="isTooLargeBatch" class="limit-error-container">
              <p class="error-text">
                <strong>Limit Exceeded:</strong> You cannot reprocess more than 25,000 products at once. 
              </p>
              <p>Please use filters to reduce the number of products below 25,000.</p>
            </div>

            <div v-else-if="isExpensiveBatch" class="high-volume-warning">
              <p class="warning-text">
                <strong>Warning: Expensive Operation</strong>
              </p>
              <p class="mb-4">
                You are about to reprocess a large volume of data ({{ selectedCount }} products). 
                This will place a heavy load on the system and will be expensive to process.
              </p>
              <label class="confirm-label">
                To confirm, please type <strong>{{ selectedCount }}</strong> in the box below:
              </label>
              <input 
                type="text" 
                v-model="confirmationCountInput" 
                class="confirm-input"
                :placeholder="selectedCount.toString()"
              />
            </div>

            <div v-else>
              <p class="mb-6">
                This will add all currently visible articles back to the queue to
                be processed again. Please confirm you'd like to continue.
              </p>
            </div>

            <div v-if="!isTooLargeBatch" class="mt-4">
              <v-select
                v-model="selectedReprocessOptions"
                :items="reprocessOptions"
                item-title="text"
                item-value="value"
                label="Refine Reprocessing (Optional)"
                hint="Select parts to focus on. Leave empty for a full reprocess."
                persistent-hint
                multiple
                chips
                closable-chips
                variant="outlined"
                :menu-props="{ zIndex: 10000 }"
              ></v-select>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="showReprocessConfirm = false">
              Cancel
            </v-btn>
            <v-btn
              color="#2c3e50"
              variant="flat"
              :disabled="isReprocessDisabled"
              @click="reprocessAllFilteredProducts"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { auth } from "@/firebase";
import { ref, computed } from "vue";
import { useProductStore } from '@/store/product-viewer/productStore.js';
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import debounce from "lodash/debounce";
import BackendCategoryTree from './BackendCategoryTree.vue';

export default {
  name: "Catalog",
  emits: ["filters-changed", "open-product"],
  components: {Multiselect, BackendCategoryTree},
  props: {
    primaryFilterKeys: Array,
    topLevelFilterOptions: Object,
    taxonomyFilterOptions: Object,
    allAttributeOptions: Array,
    products: Array,          // Catalog items to display
    totalProducts: Number,
    isLoading: Boolean,

    // Filter props
    selectedFilters: Object,  // e.g. { normalizedBrandName: [...], allium_category: [...], etc. }
    selectedCategoryPath: Array,
    selectedAttrId: String,
    selectedAttrValue: String,

    // Search inputs
    brandSearch: String,
    alliumSearch: String,
    webCategorySearch: String,

    // Pagination
    currentPage: Number,
    pageSize: Number,
    showAttributeFlags: Boolean
  },
  created() {
    this.debouncedBrandSearch = debounce(this.updateBrandSearch, 500);
    this.debouncedWebCategorySearch = debounce(this.updateWebCategorySearch, 500);
    this.debouncedAlliumSearch = debounce(this.updateAlliumSearch, 500);
          // Get an instance of the store.
    this.store = useProductStore();
  },
  data() {
    return {
      selectedAttributeObj: null,
      isLoadingLocal: false,
      selectedValueObj: null,
      selectedColorValue: null,
      showAllAttributes: false,

      imageFlagKey: 'alliumInternalData.processingDetails.processingFlags.hasRetrievableImage',
      gpcFlagKey: 'alliumInternalData.processingDetails.processingFlags.gpcBackendMappingNotOk',

      // Storing any local attribute search input
      localAttrSearch: "",

      // Used for selection mode (i.e. when the user wants to select individual items for reprocessing)
      selectMode: false,
      selectedIds: [],
      
      // Track whether the attribute dropdown is open
      attrDropdownOpen: false,
      store: {},

      confirmationCountInput: "",

      showReprocessConfirm: false,      
      selectedReprocessOptions: [],
      reprocessOptions: [
        { value: "gpc_categorization",    text: "GPC Categorization" },
        { value: "backend_categorization", text: "Backend Categorization" },
        { value: "frontend_categorization", text: "Frontend Categorization" },
        { value: "attributes",            text: "Product Attributes" },
        { value: "text_fields",           text: "Text Fields (Description, etc.)" },
        { value: "colors",                text: "Colors" },
        { value: "size",                  text: "Sizing" },
        { value: "variant_attributes",    text: "Variant Attributes" }
      ],

      // If you want to store attribute data in the component for some reason:
      attributeFilters: {
        dressCategory: {
          attributes: ['ClothingType'],
          values: { ClothingType: new Set() }
        },
        manorTaxonomy: {
          attributes: [],
          values: {}
        },
        alliumTaxonomy: {
          attributes: [],
          values: {}
        }
      }
    };
  },
  computed: {
    qualityCheckModel: {
      get() {
        const arrValue = this.selectedFilters['processingDetails.qualityFlag'];
        if (!arrValue || !arrValue[0]) return '';
        return arrValue[0];
      },
      set(newValue) {
        const normalizedValue = newValue || undefined;
        this.updateFilters({
          selectedFilters: {
            ...this.selectedFilters,
            'processingDetails.qualityFlag': [normalizedValue]
          },
          currentPage: 1
        });
      },
    },
    timeFilterModel: {
      get() {
        const arrValue = this.selectedFilters["processedTime"];
        if (!arrValue || !arrValue[0]) return '';
        return arrValue[0];
      },
      set(newValue) {
        const normalizedValue = newValue || undefined;
        this.updateFilters({
          selectedFilters: {
            ...this.selectedFilters,
            'processedTime': [normalizedValue]
          },
          currentPage: 1,
        });
      },
    },
    qualityFlagFilterModel: {
      get() {
        if (this.selectedFilters[this.imageFlagKey]) return this.imageFlagKey;
        if (this.selectedFilters[this.gpcFlagKey]) return this.gpcFlagKey;
        return '';
      },
      set(newValue) {
        const normalizedValue = newValue || undefined;
        
        let filterPayload = {};
        if (normalizedValue === this.imageFlagKey) filterPayload = { [this.imageFlagKey]: [false] };
        if (normalizedValue === this.gpcFlagKey) filterPayload = { [this.gpcFlagKey]: [true] };

        const { [this.imageFlagKey]: _, [this.gpcFlagKey]: __, ...otherFilters } = this.selectedFilters;
        this.updateFilters({
          selectedFilters: {
            ...otherFilters,
            // Add in new filter value
            ...filterPayload,
          },
          currentPage: 1,
        });
      },
    },
    taxonomyFilterModel: {
      get() {
        const taxonomyName = this.selectedFilters["alliumInternalData.taxonomyMetadata.backendTaxonomy.filename"];
        const taxonomyUpdatedAt = this.selectedFilters["alliumInternalData.taxonomyMetadata.backendTaxonomy.updatedAt"];
        if (!taxonomyName || !taxonomyUpdatedAt || !taxonomyName[0] || !taxonomyUpdatedAt[0]) return '';
        return `${taxonomyName}::${taxonomyUpdatedAt}`;
      },
      set(newValue) {
        const [name, updatedAt] = newValue?.split("::") || ["", ""];
        this.updateFilters({
          selectedFilters: {
            ...this.selectedFilters,
            'alliumInternalData.taxonomyMetadata.backendTaxonomy.filename': [name || undefined],
            'alliumInternalData.taxonomyMetadata.backendTaxonomy.updatedAt': [updatedAt || undefined],
          },
          currentPage: 1,
        });
      },
    },
    gpcBrickUnchangedModel: {
      get() {
        // Reads the current value from the central filter state and returns a simple string.
        const key = 'alliumInternalData.processingDetails.gpcBrickUnchanged';
        const arr = this.selectedFilters[key] || [];
        if (arr.includes(true)) return 'true';
        if (arr.includes(false)) return 'false';
        return ''; // Represents the "None" option
      },
      set(newValue) {
        // Takes the simple string from the dropdown and updates the central filter state with the correct array format.
        const key = 'alliumInternalData.processingDetails.gpcBrickUnchanged';
        const newArr = (newValue === 'true')
          ? [true]
          : (newValue === 'false')
            ? [false]
            : [];

        const newSelectedFilters = {
          ...this.selectedFilters,
          [key]: newArr
        };

        this.updateFilters({
          selectedFilters: newSelectedFilters,
          currentPage: 1
        });
      }
    },
    colorOptions() {
      // Gets the color options populated by the store
      return this.topLevelFilterOptions.variantColor || [];
    },
    selectedCount() {
      return this.selectMode ? this.selectedIds.length : this.totalProducts;
    },
    isExpensiveBatch() {
      const THRESHOLD = 5000;
      return this.selectMode
        ? this.selectedIds > THRESHOLD
        : this.totalProducts > THRESHOLD;
    },
    isTooLargeBatch() {
      const THRESHOLD = 25000;
      return this.selectMode
        ? this.selectedIds > THRESHOLD
        : this.totalProducts > THRESHOLD;
    },
    showAttributeFlagsComputed: {
      get() {
        // read from props or from the store’s filter
        return this.$props.showAttributeFlags; 
        // or: return this.$props.selectedFilters.showAttributeFlags 
        // if you had stored it that way
      },
      set(val) {
        this.updateFilters({
          showAttributeFlags: val,
          currentPage: 1
        });
      }
    },
    gpcBrickUnchangedComputed: {
      get() {
        // Suppose in the store’s `selectedFilters`, we store it under the key "gpcBrickUnchanged"
        // as an array, e.g. [] = none, [true] = true, [false] = false
        const arr = this.selectedFilters.gpcBrickUnchanged || [];
        if (arr.length === 0) return "";
        if (arr[0] === true) return "true";
        if (arr[0] === false) return "false";
        return "";
      },
      set(newVal) {
        // If user picks "true", we store [true]; if "false", [false], else []
        const newArr = (newVal === "true")
          ? [true]
          : (newVal === "false")
            ? [false]
            : [];
        // Then we update the store filters
        const cloned = JSON.parse(JSON.stringify(this.selectedFilters));
        cloned["alliumInternalData.processingDetails.gpcBrickUnchanged"] = newArr;

        this.updateFilters({ selectedFilters: cloned, currentPage: 1 });
      }
    },
    
    /**
     * The text shown in the custom dropdown control:
     * e.g. "Pattern (128)" if "Pattern" is selected with totalCount=128
     */
    selectedAttributeDisplay() {
      if (!this.selectedAttrIdComputed) return "";
      const found = this.allAttributeOptions.find(a => a.attrId === this.selectedAttrIdComputed);
      return found ? `${found.displayName} (${found.totalCount})` : "";
    },

    /**
     * The local filtered array for the dropdown's <ul>
     */
    filteredAttributeOptions() {
      let base = this.allAttributeOptions || [];
      const s = this.localAttrSearch.trim().toLowerCase();
      if (s) {
        base = base.filter(a => {
          return (
            (a.displayName && a.displayName.toLowerCase().includes(s)) ||
            (a.attrId && a.attrId.toLowerCase().includes(s))
          );
        });
      }
      return base;
    },

    /**
     * "v-model style" computed for selectedFilters.
     * This merges watchers + computed approach to keep local "internalFilters" in sync.
     */
    selectedFiltersComputed: {
      get() {
        return this.selectedFilters;
      },
      set(newVal) {
        const newObj = JSON.parse(JSON.stringify(newVal));
        this.updateFilters({
          selectedFilters: newObj,
          currentPage: 1
        });
      }
    },
    selectedCategoryPathComputed: {
      get() {
        return this.selectedCategoryPath;
      },
      set(val) {
        this.updateFilters({ selectedCategoryPath: val });
      }
    },
    selectedAttrIdComputed: {
      get() { return this.selectedAttrId; },
      set(val) {
        // Clear the value if attribute changes
        this.updateFilters({ selectedAttrId: val, selectedAttrValue: "" });
      }
    },
    selectedAttrValueComputed: {
      get() { return this.selectedAttrValue; },
      set(newVal) {
        this.updateFilters({ selectedAttrValue: newVal, currentPage: 1 });
      }
    },

    brandSearchComputed: {
      get() { return this.brandSearch; },
      set(val) {
        this.updateFilters({ brandSearch: val, currentPage: 1 });
      }
    },
    alliumSearchComputed: {
      get() { return this.alliumSearch; },
      set(val) {
        this.updateFilters({ alliumSearch: val, currentPage: 1 });
      }
    },
    webCategorySearchComputed: {
      get() { return this.webCategorySearch; },
      set(val) {
        this.updateFilters({ webCategorySearch: val, currentPage: 1 });
      }
    },

    currentPageComputed: {
      get() { return this.currentPage; },
      set(val) { this.updateFilters({ currentPage: val }); }
    },
    pageSizeComputed: {
      get() { return this.pageSize; },
      set(val) {
        this.updateFilters({ pageSize: val, currentPage: 1 });
      }
    },

    // Do we have any filters currently active?
    hasActiveFilters() {
      const anyPrimary = Object.values(this.selectedFiltersComputed).some(arr => arr.length > 0);
      const singleAttr = !!(this.selectedAttrIdComputed && this.selectedAttrValueComputed);
      return anyPrimary || singleAttr;
    },

    // Pagination calculations
    totalPages() {
      return Math.ceil(this.totalProducts / this.pageSizeComputed);
    },
    pagesToShow() {
      const total = this.totalPages;
      const current = this.currentPageComputed;
      if (total <= 10) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = [];
      pages.push(1);
      let start = Math.max(2, current - 4);
      let end = Math.min(total - 1, current + 4);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (end < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    },

    // Show the first ~8 attributes or expand if toggled
    visibleAttributeOptions() {
      // Start with the entire array
      let base = this.allAttributeOptions || [];

      // If user typed something into localAttrSearch, filter here
      const s = this.localAttrSearch.trim().toLowerCase();
      if (s) {
        base = base.filter(a => {
          // a.displayName or a.attrId might match
          return (
            (a.displayName && a.displayName.toLowerCase().includes(s)) ||
            (a.attrId && a.attrId.toLowerCase().includes(s))
          );
        });
      }

      // If "showAllAttributes" is false => slice top 8
      if (!this.showAllAttributes) {
        return base.slice(0, 8);
      }
      // Otherwise return all
      return base;
    },

    // The list of values for the chosen attribute (in the single-attribute bar)
    currentAttrValues() {
      if (!this.selectedAttrIdComputed) return [];
      const found = this.allAttributeOptions.find(a => a.attrId === this.selectedAttrIdComputed);
      return found ? found.values : [];
    },

    /**
     * Build subcategories from the "allium_category" top-level filter options
     * to display as clickable subcategory names
     */
    subcategories() {
      const arr = this.topLevelFilterOptions.allium_category || [];
      const tree = {};

      // Build a nested structure
      for (const item of arr) {
        const path = item.value.split("|");
        let node = tree;
        for (const seg of path) {
          if (!node[seg]) {
            node[seg] = { __count: 0, __children: {} };
          }
          node[seg].__count += item.count;
          node = node[seg].__children;
        }
      }

      // Navigate down to the current selected path
      let node2 = tree;
      for (const level of this.selectedCategoryPathComputed) {
        if (node2[level]) {
          node2 = node2[level].__children;
        } else {
          return [];
        }
      }

      // Collect children
      const children = [];
      for (const k in node2) {
        if (k !== '__count' && k !== '__children') {
          children.push({ name: k, totalCount: node2[k].__count });
        }
      }
      children.sort((a, b) => b.totalCount - a.totalCount);
      return children;
    },

    /**
     * Build a map of backend category names to their counts
     * for use in the tree component
     */
    backendCategoryCounts() {
      const countsMap = {};
      const backendCategoryOptions = this.topLevelFilterOptions.backendCategory || [];
      backendCategoryOptions.forEach(opt => {
        if (opt.value) {
          countsMap[opt.value] = opt.count || 0;
        }
      });
      return countsMap;
    },
    isReprocessDisabled() {
      if (this.isTooLargeBatch) {
        return true;
      }
      
      if (this.isExpensiveBatch) {
        return this.confirmationCountInput !== String(this.selectedCount);
      }

      return false;
    }
  },
  watch: {
      // Syncs local state when the `selectedFilters` prop from the store changes.
      // This is primarily for the color filter dropdown.
      selectedFilters: {
          deep: true,
          immediate: true,
          handler(newFilters) {
              const colorFilter = newFilters.variantColor || [];
              const newColor = colorFilter.length > 0 ? colorFilter[0] : null;
              if (this.selectedColorValue !== newColor) {
                  this.selectedColorValue = newColor;
              }
          }
      },
      // This watcher now only reacts to direct user input on the color dropdown.
      selectedColorValue(newVal, oldVal) {
          if (newVal === oldVal) return; // Prevents firing if the value hasn't changed.

          const newFilters = { ...this.selectedFilters };
          newFilters.variantColor = newVal ? [newVal] : [];
          this.updateFilters({
              selectedFilters: newFilters,
              currentPage: 1
          });
      },

      // --- ATTRIBUTE FILTER SYNCHRONIZATION ---
      // This logic correctly handles the two-way data flow for the attribute selectors.

      // [1] Fires when a USER selects an attribute.
      selectedAttributeObj(newAttr, oldAttr) {
          if (newAttr?.attrId === oldAttr?.attrId) return;
          this.selectedValueObj = null;
          this.updateFilters({
              selectedAttrId: newAttr ? newAttr.attrId : '',
              selectedAttrValue: '',
              currentPage: 1
          });
      },

      // [2] Fires when a USER selects a value.
      selectedValueObj(newValue, oldValue) {
          if (newValue?.valueCode === oldValue?.valueCode) return;
          this.updateFilters({
              selectedAttrValue: newValue ? newValue.valueCode : '',
              currentPage: 1
          });
      },

      // [3] Syncs local state when the `selectedAttrId` PROP changes.
      selectedAttrId: {
          handler(newId) {
              if (this.selectedAttributeObj?.attrId !== newId) {
                  this.selectedAttributeObj = newId ? this.allAttributeOptions.find(opt => opt.attrId === newId) || null : null;
              }
          },
          immediate: true
      },

      // [4] Syncs local state when the `selectedAttrValue` PROP changes.
      selectedAttrValue: {
          handler(newValue) {
              if (this.selectedValueObj?.valueCode !== newValue) {
                  if (newValue && this.selectedAttributeObj) {
                      this.selectedValueObj = this.selectedAttributeObj.values.find(v => v.valueCode === newValue) || null;
                  } else {
                      this.selectedValueObj = null;
                  }
              }
          },
          immediate: true
      }
  },

  methods: {
    isFilterActive(key, value) {
      // Checks if a filter checkbox should be active based on the prop from the store.
      return this.selectedFilters[key]?.includes(value) ?? false;
    },
    toggleFilter(key, value) {
      // Gets a copy of the current values for the filter key from props.
      const currentValues = this.selectedFilters[key] ? [...this.selectedFilters[key]] : [];
      const valueIndex = currentValues.indexOf(value);

      if (valueIndex > -1) {
        // Value exists, so remove it (unchecks the box).
        currentValues.splice(valueIndex, 1);
      } else {
        // Value does not exist, so add it (checks the box).
        currentValues.push(value);
      }

      // Create a new selectedFilters object with the updated array.
      const newSelectedFilters = {
        ...this.selectedFilters,
        [key]: currentValues
      };

      // Emit the change using the existing updateFilters method.
      this.updateFilters({
        selectedFilters: newSelectedFilters,
        currentPage: 1
      });
    },
    
    toggleBackendCategory(categoryName) {
      const key = 'backendCategory';
      const currentValues = [...(this.selectedFilters[key] || [])];
      const idx = currentValues.indexOf(categoryName);

      if (idx > -1) {
        currentValues.splice(idx, 1);
      } else {
        currentValues.push(categoryName);
      }

      const newSelectedFilters = {
        ...this.selectedFilters,
        [key]: currentValues
      };

      this.updateFilters({
        selectedFilters: newSelectedFilters,
        currentPage: 1
      });
    },
    
    updateBrandSearch(value) {
      this.updateFilters({ brandSearch: value, currentPage: 1 });
    },
    updateWebCategorySearch(value) {
      this.updateFilters({ webCategorySearch: value, currentPage: 1 });
    },
    updateAlliumSearch(value) {
      this.updateFilters({ alliumSearch: value, currentPage: 1 });
    },
    getDisplayImage(product) {
      const activeColorFilter = this.selectedFiltersComputed.variantColor?.[0];

      if (activeColorFilter && product.variants?.length > 0) {
        // Find the first variant whose PRIMARY color matches the active filter
        const matchingVariant = product.variants.find(
          (v) =>
            v.variantDiscriminators?.color?.attrValues?.[0]?.value ===
            activeColorFilter
        );

        // If we found a matching variant with an image, use it
        if (matchingVariant && matchingVariant.imageURLs?.[0]) {
          return matchingVariant.imageURLs[0];
        }
      }

      // Fallback to the default product image logic
      return product.image;
    },
    attributeLabel(option) {
      // e.g. “Pattern (128)”
      return `${option.displayName} (${option.totalCount || 0})`;
    },
    valueLabel(val) {
      // e.g. “Plain (80)”
      return `${val.displayValue} (${val.count || 0})`;
    },
    // Toggle the custom attribute dropdown
    toggleAttributeDropdown() {
      this.attrDropdownOpen = !this.attrDropdownOpen;
    },

    // Called when user clicks an attribute in the dropdown list
    selectAttribute(attr) {
      this.attrDropdownOpen = false;
      // Force the store to re-run with new attribute
      this.selectedAttrIdComputed = attr.attrId;
    },

    // Rebuild local attribute filters if needed
    buildAttributeFilters() {
      try {
        this.attributeFilters = {
          dressCategory: {
            attributes: ['ClothingType'],
            values: { ClothingType: new Set() }
          },
          manorTaxonomy: {
            attributes: [],
            values: {}
          },
          alliumTaxonomy: {
            attributes: [],
            values: {}
          }
        };
        // Example usage: iterate over `products` to gather attribute info
        if (this?.products?.length) {
          this.products.forEach(prod => {
            this.processProductAttributes(prod);
            if (prod.variants) {
              prod.variants.forEach(v => {
                if (v.attributes) {
                  this.processVariantAttributes(v);
                }
              });
            }
          });
        }
        // Convert sets -> arrays, etc.
        for (const cat in this.attributeFilters) {
          this.attributeFilters[cat].attributes.sort();
          for (const attr in this.attributeFilters[cat].values) {
            this.attributeFilters[cat].values[attr] = Array.from(
              this.attributeFilters[cat].values[attr]
            ).sort();
          }
        }
      } catch (err) {
        console.error('Error building attribute data:', err);
      }
    },
    processProductAttributes(prod) {
      // Example logic to store certain attribute data
      if (prod.attributes && prod.attributes['20001940']) {
        const val = prod.attributes['20001940'];
        const clothingType = typeof val === 'object' ? val.value : val;
        if (clothingType) {
          this.attributeFilters.dressCategory.values.ClothingType.add(clothingType);
        }
      }
    },
    processVariantAttributes(variant) {
      // If you have variant-level attributes to handle, do it here
      // This is just a placeholder example
      if (variant.attributes && variant.attributes['60001734']) {
        // e.g. Trousers Width
        const widthVal = variant.attributes['60001734'].attrValues?.[0]?.value;
        // do something with widthVal
      }
    },

    /**
     * Return a filtered array of top-level filter options based on user search
     */
    visibleFilterOptions(fkey) {
      const arr = this.topLevelFilterOptions[fkey] || [];

      // If the user typed something into the brand search box
      if (fkey === "brandId" && this.brandSearchComputed) {
        const s = this.brandSearchComputed.toLowerCase();
        return arr.filter(x => x.value && x.value.toLowerCase().includes(s));
      }

      // If the user typed something into the webCategory search box
      if (fkey === "webCategory" && this.webCategorySearchComputed) {
        const s = this.webCategorySearchComputed.toLowerCase();
        return arr.filter(x => x.value && x.value.toLowerCase().includes(s));
      }

      // If the user typed something into the “alliumSearch” 
      // (like "materialSearch")
      if (fkey === "materialName" && this.alliumSearchComputed) {
        const s = this.alliumSearchComputed.toLowerCase();
        return arr.filter(x => x.value && x.value.toLowerCase().includes(s));
      }

      // Otherwise, return everything
      return arr;
    },

    /**
     * Called when we modify any filter in the UI
     */
    updateFilters(partial) {
        const newState = {
            // Base state from props
            brandSearch: this.brandSearch,
            alliumSearch: this.alliumSearch,
            webCategorySearch: this.webCategorySearch,
            selectedFilters: { ...this.selectedFilters },
            selectedCategoryPath: [...this.selectedCategoryPath],
            selectedAttrId: this.selectedAttrId,
            selectedAttrValue: this.selectedAttrValue,
            currentPage: this.currentPage,
            pageSize: this.pageSize,
            showAttributeFlags: this.showAttributeFlags,
            // Apply the changes from the specific UI interaction
            ...partial
        };

        this.selectMode = false;
        this.selectedIds = [];
        this.$emit("filters-changed", newState);
    },

    /**
     * Clicking a subcategory in the "subcategory-list"
     */
    drillDownCategory(name) {
      const newPath = [...this.selectedCategoryPathComputed, name];
      this.updateFilters({
        selectedCategoryPath: newPath,
        currentPage: 1
      });
    },

    /**
     * Clicking a breadcrumb in the category path
     */
    goToCategoryLevel(idx) {
      const newPath = this.selectedCategoryPathComputed.slice(0, idx + 1);
      this.selectedCategoryPathComputed = newPath;
    },

    removeFilter(key, val) {
      const arr = this.selectedFiltersComputed[key] || [];
      const newArr = arr.filter(x => x !== val);
      this.updateFilters({
        selectedFilters: { ...this.selectedFiltersComputed, [key]: newArr }
      });
    },

    clearSingleAttribute() {
      this.updateFilters({ selectedAttrId: "", selectedAttrValue: "" });
    },

    clearAllFilters() {
      const cleared = {
        brandId: [],
        webCategory: [],
        materialName: [],
        variantColor: []
      };
      this.updateFilters({
        selectedFilters: cleared,
        selectedAttrId: "",
        selectedAttrValue: "",
        selectedCategoryPath: [],
        brandSearch: "",
        alliumSearch: "",
        webCategorySearch: "",
        currentPage: 1
      });
    },

    // Pagination
    goToPage(page) {
      this.currentPageComputed = page;
    },
    prevPage() {
      if (this.currentPageComputed > 1) {
        this.goToPage(this.currentPageComputed - 1);
      }
    },
    nextPage() {
      if (this.currentPageComputed < this.totalPages) {
        this.goToPage(this.currentPageComputed + 1);
      }
    },

    /**
     * Default fallback for broken images
     */
    onImageError(e) {
      e.target.src = "https://placehold.co/200x200?text=No+Image";
    },


    formatFilterKey(k) {
      if (k === "normalizedBrandName") return "Brand";
      if (k === "brandId") return "Brand";
      if (k === "allium_category") return "Allium Category";
      if (k === "webCategoryCode") return "Web Category";
      if (k === "webCategory") return "Web Category";
      if (k === "normalizedMaterial") return "Material";
      if (k === "materialName") return "Material";
      if (k === "variantColor") return "Color";
      if (k === "backendCategory") return "Backend Category";
      if (k === "alliumInternalData.processingDetails.gpcBrickUnchanged") return "GPC Brick Unchanged";
      if (k === "qualityFlag") return "Quality Check";
      if (k === "processedTime") return "Processed Time";
      return k;
    },
    getAttrDisplayName(attrId) {
      const found = this.allAttributeOptions.find(x => x.attrId === attrId);
      return found ? found.displayName : attrId;
    },

    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      this.selectedIds = [];
    },

    handleProductClick(product) {
      if (this.selectMode) {
        // If the user is currently selecting items for granular processing, add/remove from selection
        this.selectedIds.includes(product.id)
          ? this.selectedIds = this.selectedIds.filter(id => id !== product.id)
          : this.selectedIds = [...this.selectedIds, product.id]
      } else {
        // Otherwise, the user is attempting to navigate to the item's PDP
        this.$emit("open-product", { productId: product.id });
      }
    },

    rebuildFilterCounts() {
      this.isLoadingLocal = true;
      fetch("https://save-manor-frontend-reference-data-998838505944.europe-west6.run.app/")
        .then(() => {
          store.initCatalogData();
          alert('Rebuilt successfully.')
          this.isLoadingLocal = false;
        })
        .catch(() => 
        {
          alert('Failed to rebuild filters')
          this.isLoadingLocal = false;
        });
    },

    openReprocessModal() {
      // Don't open if there are no products
      if (this.products.length === 0) return;
      this.selectedReprocessOptions = []; // Reset options
      this.showReprocessConfirm = true;
      this.confirmationCountInput = "";
    },

async reprocessAllFilteredProducts() {
      if (this.isTooLargeBatch) {
        alert("Cannot process more than 25,000 products.");
        return;
      }

      if (this.totalProducts === 0) {
        console.error("No products match the filters.");
        return;
      }
      
      const payload = {
         filters: {
           selectedFilters: this.selectedFilters,
           selectedCategoryPath: this.selectedCategoryPath,
           selectedAttrId: this.selectedAttrId,
           selectedAttrValue: this.selectedAttrValue,
           brandSearch: this.brandSearch,
           alliumSearch: this.alliumSearch,
           webCategorySearch: this.webCategorySearch,
           showAttributeFlags: this.showAttributeFlags
         },
         selectedIds: this.selectedIds,
         partialProcessingSteps: this.selectedReprocessOptions,
         userEmail: auth.currentUser.email,
      };

      console.log("Dispatching reprocess action with payload:", payload);

      try {
        const result = await this.store.reprocessProducts(payload);
        console.log('Successfully initiated reprocessing:', result.message);
        alert(`Reprocessing started for all ${this.selectedCount} matching products.`);
      } catch (error) {
        console.error('Failed to dispatch reprocess action:', error);
        alert(`Error: ${error.message}`);
      } finally {
        this.showReprocessConfirm = false;
      }
    },
  }
};
</script>


<style scoped>
.top-filters-wrapper,
.sidebar-wrapper {
  position: relative;
}

/* The overlay itself */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.85); /* Semi-transparent white */
  z-index: 10; /* Ensure it's on top of the content */
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px; /* Match the container's border-radius */
}

/* Make the spinner slightly larger inside the overlay */
.loading-overlay .loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border-width: 3px;
}

/* Style for disabled links (non-input elements) */
.disabled-link {
  pointer-events: none; /* Prevents click events */
  cursor: not-allowed;
  opacity: 0.6;
}

/* General styling for disabled form elements */
select:disabled,
input:disabled,
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Specific override for vue-multiselect disabled state for better UX */
:deep(.multiselect.multiselect--disabled) {
  background: #f1f3f5;
  opacity: 0.7;
  pointer-events: none;
}
:deep(.multiselect.multiselect--disabled .multiselect__select) {
  background: transparent;
}

.catalog-page {
  min-height: 100vh;
  width: 100vw;
  background-color: #f9fafb;
  padding-top: 8px; /* e.g. if you have a fixed header */
}

.select-mark {
  font-size: 2rem;
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 9; /* since this is a floating element
                  we need to set a z-index to ensure
                  it renders above the product card */
}

/* Category nav up top */
.category-navigation {
  background: #fff;
  padding: 1rem 2rem;
  margin: 0 20px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.category-crumbs {
  margin-bottom: 0.5rem;
}
.breadcrumb {
  margin-right: 8px;
  color: #2563eb;
  cursor: pointer;
}
.breadcrumb:hover {
  text-decoration: underline;
}
.subcategory-list {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.subcategory-link {
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
}
.subcategory-link:hover {
  background: #e5e7eb;
}

/* Single-attribute filter row at the top */
.top-attributes-container {
  position: relative;
  margin: 0 20px 20px;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.toggle-attributes-button {
  background-color: #2563eb;
  color: #fff;
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
}
.toggle-attributes-button:hover {
  background-color: #1e4fb8;
}

.attribute-dropdown {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  display: inline-flex;
  flex-direction: column;
  min-width: 200px;
}
.attribute-dropdown label {
  margin-bottom: 0.25rem;
  font-weight: 600;
  font-size: 0.85rem;
}
.attribute-dropdown select {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.3rem;
  font-size: 0.85rem;
}
.top-attributes-bar::-webkit-scrollbar {
  height: 6px;
}
.top-attributes-bar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}

/* Main layout */
.catalog-layout {
  display: flex;
  gap: 2rem;
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.filters-sidebar {
  width: 300px;
  flex-shrink: 0;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  height: fit-content;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

.filter-section {
  margin-bottom: 2rem;
}
.filter-section h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #374151;
}
.search-box {
  margin-bottom: 0.75rem;
}
.search-box input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
}
.search-box input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37,99,235,0.1);
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;
}
.filter-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #4b5563;
}
.filter-option input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

/* Active Filters */
.active-filters {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
}
.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: #e5e7eb;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #374151;
}
.remove-filter {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.25rem;
}
.remove-filter:hover {
  color: #ef4444;
}
.clear-filters {
  width: 100%;
  padding: 0.5rem;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}
.clear-filters:hover {
  background-color: #e5e7eb;
}

/* Products section */
.products-content {
  flex: 1;
  min-width: 0;
}
.results-summary {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
}
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.75rem;
  margin-bottom: 2rem;
}

.product-card {
  position: relative;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.product-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.08);
}

.product-image {
  position: relative;
  padding-top: 100%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow: hidden;
}

.product-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.02) 100%);
  pointer-events: none;
}

.product-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img {
  transform: scale(1.05);
}

.product-info {
  padding: 1.25rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.product-info h3 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.85rem;
}

.product-brand {
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin: 0;
}

.product-category {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Skeleton Loaders */
.skeleton-card {
  pointer-events: none;
  cursor: default;
}

.skeleton-card:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.04);
}

.skeleton-image {
  background: linear-gradient(90deg, #f0f0f0 0%, #e8e8e8 20%, #f0f0f0 40%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite linear;
}

.skeleton-title,
.skeleton-brand,
.skeleton-category {
  background: linear-gradient(90deg, #f0f0f0 0%, #e8e8e8 20%, #f0f0f0 40%, #f0f0f0 100%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite linear;
  border-radius: 6px;
}

.skeleton-title {
  height: 1rem;
  width: 90%;
  margin-bottom: 0.75rem;
}

.skeleton-brand {
  height: 0.8rem;
  width: 65%;
  margin-bottom: 0.5rem;
}

.skeleton-category {
  height: 0.75rem;
  width: 75%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Loading & No products */
.loading-spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.no-products-message {
  text-align: center;
  margin: 2rem 0;
  font-weight: 500;
  color: #555;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}
.pagination {
  list-style: none;
  display: flex;
  gap: 8px;
  padding: 0;
}
.pagination li {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  color: #374151;
  background-color: #fff;
  transition: background-color 0.2s;
}
.pagination li:hover:not(.active):not(.dots):not(.disabled) {
  background-color: #f3f4f6;
}
.pagination li.active {
  background-color: #2563eb;
  color: #fff;
  cursor: default;
  border-color: #2563eb;
}
.pagination li.dots {
  cursor: default;
}
.pagination li.disabled {
  opacity: 0.4;
  cursor: default;
}
.pagination li.arrow {
  width: 35px;
  text-align: center;
}

/* Page size container below pagination */
.page-size-container {
  text-align: center;
  margin-bottom: 2rem;
}
.page-size-container .filter-field {
  display: inline-block;
  margin-top: 0.5rem;
}
.page-size-container label {
  margin-right: 8px;
  font-weight: 600;
  font-size: 0.85rem;
}
.page-size-container select {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.3rem;
  font-size: 0.85rem;
}

:deep(.multiselect__option--highlight) {
  background-color: #2563eb !important;
  color: #fff !important;
}

.attribute-multiselect .multiselect {
  min-width: 300px; /* or 400px, whatever fits your text */
  max-width: 100%;
}

/* 2) Let text wrap (remove horizontal scrollbar) */
.attribute-multiselect .multiselect__content {
  white-space: normal; /* allows multiple lines if needed */
  overflow-x: hidden;  /* remove horizontal scrollbar */
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.limit-error-container {
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  color: #b91c1c;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.error-text {
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 0.5rem;
}

.high-volume-warning {
  background-color: #fff1f2; 
  border: 1px solid #e11d48;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.warning-text {
  color: #dc2626; 
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.confirm-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #374151;
  font-size: 0.9rem;
}

.confirm-input {
  width: 100%;
  padding: 0.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  font-size: 1rem;
}

.confirm-input:focus {
  outline: none;
  border-color: #dc2626; 
}
</style>
